'use client';

import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

const PORTFOLIO_ITEMS = [
{
  id: 'port-001',
  title: 'Complete bathroom installation',
  category: 'Bathroom',
  date: 'Mar 2026',
  description: 'Full bathroom suite replacement including walk-in shower, freestanding bath, and underfloor heating in Clapham.',
  src: "https://images.unsplash.com/photo-1649083047090-fd89bf55b893",
  alt: 'Modern white bathroom with walk-in shower and freestanding bath'
},
{
  id: 'port-002',
  title: 'Boiler replacement — Worcester Bosch',
  category: 'Heating',
  date: 'Jan 2026',
  description: 'New A-rated Worcester Bosch combi boiler install, replacing 20-year-old system in a Victorian terrace.',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7284827-1765086343340.png",
  alt: 'Modern white combi boiler mounted on a wall'
},
{
  id: 'port-003',
  title: 'Underfloor heating — open plan kitchen',
  category: 'Heating',
  date: 'Feb 2026',
  description: 'Wet underfloor heating system installed across 45m² open plan kitchen-diner extension in Balham.',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1761e050a-1768469148243.png",
  alt: 'Open plan kitchen with polished concrete floor'
},
{
  id: 'port-004',
  title: 'Emergency pipe repair',
  category: 'Emergency',
  date: 'Apr 2026',
  description: 'Burst pipe repair in loft space — same-day emergency response, damage contained within 2 hours.',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ea2aa366-1767678265006.png",
  alt: 'Plumber working on copper pipes in a loft space'
},
{
  id: 'port-005',
  title: 'En suite installation',
  category: 'Bathroom',
  date: 'Dec 2025',
  description: 'New en suite bathroom in converted loft, including all pipework routing through existing structure.',
  src: "https://images.unsplash.com/photo-1722152749961-20be206705c8",
  alt: 'Small en suite bathroom with rainfall shower and grey tiles'
},
{
  id: 'port-006',
  title: 'Radiator installation — period property',
  category: 'Heating',
  date: 'Nov 2025',
  description: 'Bespoke column radiators installed throughout a Victorian property, sympathetic to original features.',
  src: "https://images.unsplash.com/photo-1586766079570-ea5a22cfa8a6",
  alt: 'Ornate cast iron column radiator on a white wall'
},
{
  id: 'port-007',
  title: 'Power flushing — 8-radiator system',
  category: 'Maintenance',
  date: 'Oct 2025',
  description: 'Full power flush on central heating system with 8 radiators, chemical inhibitor and magnetic filter installed.',
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae7dcd60-1772363607632.png",
  alt: 'Plumber connecting power flushing equipment to central heating system'
},
{
  id: 'port-008',
  title: 'Outdoor tap installation',
  category: 'General',
  date: 'Sep 2025',
  description: 'Frost-protected outdoor tap with isolation valve fitted to rear of terraced house in Stockwell.',
  src: "https://images.unsplash.com/photo-1596999865489-9d8237de739a",
  alt: 'Chrome outdoor garden tap mounted on brick wall'
}];


const CATEGORIES = ['All', 'Bathroom', 'Heating', 'Emergency', 'Maintenance', 'General'];

export default function PortfolioTab() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const filtered = activeCategory === 'All' ?
  PORTFOLIO_ITEMS :
  PORTFOLIO_ITEMS.filter((p) => p.category === activeCategory);

  const openLightbox = (idx: number) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prevImage = () => setLightboxIdx((i) => i !== null ? Math.max(0, i - 1) : null);
  const nextImage = () => setLightboxIdx((i) => i !== null ? Math.min(filtered.length - 1, i + 1) : null);

  return (
    <div>
      {/* Category filter */}
      <div className="flex items-center gap-2 mb-5 overflow-x-auto scrollbar-hide pb-1">
        {CATEGORIES.map((cat) =>
        <button
          key={`portcat-${cat}`}
          onClick={() => setActiveCategory(cat)}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
          activeCategory === cat ?
          'bg-plum-800 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-plum-200 hover:text-plum-700'}`
          }>

            {cat}
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
        {filtered.map((item, i) =>
        <button
          key={item.id}
          onClick={() => openLightbox(i)}
          className="group relative aspect-square rounded-2xl overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-plum-400">

            <AppImage
            src={item.src}
            alt={item.alt}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            className="object-cover transition-transform duration-300 group-hover:scale-105" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-3">
              <p className="text-white font-semibold text-xs leading-tight">{item.title}</p>
              <p className="text-white/60 text-xs">{item.date}</p>
            </div>
            <div className="absolute top-2 right-2 w-7 h-7 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn size={13} className="text-white" />
            </div>
            <div className="absolute top-2 left-2">
              <span className="px-2 py-0.5 bg-black/40 backdrop-blur-sm rounded-full text-white text-xs">
                {item.category}
              </span>
            </div>
          </button>
        )}
      </div>

      {filtered.length === 0 &&
      <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <p className="text-gray-400 text-sm">No portfolio items in this category</p>
        </div>
      }

      {/* Lightbox */}
      {lightboxIdx !== null &&
      <div
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
        onClick={closeLightbox}>

          <div
          className="relative max-w-3xl w-full bg-gray-900 rounded-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}>

            <button
            onClick={closeLightbox}
            className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors">

              <X size={16} />
            </button>

            <div className="relative aspect-video">
              <AppImage
              src={filtered[lightboxIdx].src}
              alt={filtered[lightboxIdx].alt}
              fill
              sizes="(max-width: 768px) 100vw, 768px"
              className="object-cover" />

            </div>

            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-700 text-white text-base mb-1">{filtered[lightboxIdx].title}</p>
                  <p className="text-gray-400 text-sm leading-relaxed">{filtered[lightboxIdx].description}</p>
                </div>
                <span className="text-gray-500 text-xs whitespace-nowrap">{filtered[lightboxIdx].date}</span>
              </div>
            </div>

            {/* Nav arrows */}
            <button
            onClick={prevImage}
            disabled={lightboxIdx === 0}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 rounded-full flex items-center justify-center text-white disabled:opacity-30 hover:bg-black/70 transition-colors">

              <ChevronLeft size={18} />
            </button>
            <button
            onClick={nextImage}
            disabled={lightboxIdx === filtered.length - 1}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/50 rounded-full flex items-center justify-center text-white disabled:opacity-30 hover:bg-black/70 transition-colors">

              <ChevronRight size={18} />
            </button>

            {/* Counter */}
            <div className="absolute bottom-16 right-4 px-2 py-1 bg-black/50 rounded-full text-white text-xs tabular-nums">
              {lightboxIdx + 1} / {filtered.length}
            </div>
          </div>
        </div>
      }
    </div>);

}